import {
  normalizeImageRetouch,
  hasImageRetouch,
  buildRetouchWarps,
  selectedRetouchFaces,
  selectedRetouchBodies,
  retouchParameterAvailable,
  retouchPointsVisible,
  type RetouchParameter,
  FACE_OVAL,
  FACE_LEFT_EYE,
  FACE_RIGHT_EYE,
  FACE_LEFT_BROW,
  FACE_RIGHT_BROW,
  FACE_LIPS,
  FACE_INNER_LIPS,
  type ImageRetouch,
  type RetouchPoint,
} from "./image-retouch";

const vertex = `attribute vec2 position; varying vec2 uv; void main(){uv=vec2((position.x+1.)*.5,(1.-position.y)*.5);gl_Position=vec4(position,0.,1.);}`;
const fragment = `precision highp float;
varying vec2 uv;
uniform sampler2D image, warps, masks, bodyMask, makeup;
uniform int count;
uniform float rows, aspect;
uniform vec2 pixel;
uniform vec4 skin; // smooth, whiten, even, warmth
uniform vec4 detail; // eye brightness, teeth, clarity, body smooth
uniform float bodyWhiten;
vec4 sampleAt(vec2 p){vec4 q=texture2D(image,clamp(p,vec2(0.),vec2(1.)));return vec4(q.rgb/max(q.a,.00001),q.a);}
void main(){
 vec2 p=uv*vec2(aspect,1.);
 for(int j=0;j<384;j++) {if(j>=count) break; float row=(float(count-1-j)+.5)/rows;
  vec4 a=texture2D(warps,vec2(1./6.,row)), b=texture2D(warps,vec2(.5,row)), c=texture2D(warps,vec2(5./6.,row));
  float co=cos(c.y),si=sin(c.y);vec2 q=mat2(co,-si,si,co)*(p-a.xy);
  float d=length(q/a.zw);if(d>=1.)continue;float f=1.-d;f=f*f*(3.-2.*f);
  q=(q-b.zw*f)/(vec2(1.)+b.xy*f);float an=-c.x*f; q=mat2(cos(an),sin(an),-sin(an),cos(an))*q;
  p=a.xy+mat2(co,si,-si,co)*q;
 }
 p.x/=aspect;
 vec4 original=sampleAt(p),m=texture2D(masks,p),bm=texture2D(bodyMask,p);
 vec3 center=original.rgb,sum=vec3(0.);float weight=0.;vec2 radius=pixel*max(1.,.004/pixel.y);
 for(int y=-2;y<=2;y++)for(int x=-2;x<=2;x++){vec3 v=sampleAt(p+vec2(float(x),float(y))*radius).rgb;float w=exp(-dot(v-center,v-center)*28.-float(x*x+y*y)*.22);sum+=v*w;weight+=w;}
 vec3 blur=sum/max(weight,.001);float gate=smoothstep(-.03,.08,center.r-center.b)*smoothstep(.08,.25,center.r);
 float smoothness=clamp(m.r*skin.x+bm.r*detail.w*gate+bm.g,0.,.92);
 vec3 rgb=mix(center,blur,smoothness);rgb+=m.r*detail.z*(center-blur)*1.2;
 rgb=mix(rgb,vec3(dot(blur,vec3(.299,.587,.114)))+mix(center,blur,.6)-vec3(dot(center,vec3(.299,.587,.114))),m.r*skin.z*.5);
 float white=m.r*skin.y+bm.r*bodyWhiten*gate;
 rgb+=white*(vec3(1.)-rgb)*.28;rgb+=m.r*skin.w*vec3(.055,.005,-.045);
 rgb+=bm.g*(vec3(1.)-rgb)*.07;
 rgb+=m.g*detail.x*(vec3(1.)-rgb)*.2;
 float tooth=m.b*detail.y*smoothstep(.2,.55,dot(center,vec3(.299,.587,.114)))*(1.-smoothstep(.12,.35,center.r-center.b));
 rgb=mix(rgb,mix(vec3(dot(rgb,vec3(.299,.587,.114))),vec3(1.),.3),tooth*.8);
 vec4 paint=texture2D(makeup,p);rgb=rgb*(1.-paint.a)+paint.rgb;
 gl_FragColor=vec4(clamp(rgb,0.,1.),original.a);
}`;
function canvas(w: number, h: number) {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}
function polygon(
  ctx: CanvasRenderingContext2D,
  p: RetouchPoint[],
  ids: readonly number[],
  begin = true,
) {
  if (begin) ctx.beginPath();
  ids.forEach((i, j) =>
    j ? ctx.lineTo(p[i].x, p[i].y) : ctx.moveTo(p[i].x, p[i].y),
  );
  ctx.closePath();
}
function ellipse(
  ctx: CanvasRenderingContext2D,
  p: RetouchPoint,
  rx: number,
  ry: number,
) {
  ctx.beginPath();
  ctx.ellipse(
    p.x,
    p.y,
    Math.max(0.001, rx),
    Math.max(0.001, ry),
    0,
    0,
    Math.PI * 2,
  );
  ctx.fill();
}

let retouchCanvas: HTMLCanvasElement | null = null;
/** 同一 shader 用于预览和原始分辨率导出；识别结果只做一次，不按滑块重复运行模型。 */
export async function applyImageRetouchToCanvas(
  target: HTMLCanvasElement,
  input?: ImageRetouch | null,
  signal?: AbortSignal,
) {
  if (!hasImageRetouch(input)) return;
  signal?.throwIfAborted();
  const r = normalizeImageRetouch(input),
    v = { ...r.values };
  for (const [group, enabled] of Object.entries(r.groups))
    if (!enabled)
      for (const key of Object.keys(v))
        if (RETOUCH_GROUP_LOOKUP[key] === group) v[key as keyof typeof v] = 0;
  for (const key of Object.keys(v) as RetouchParameter[])
    if (!retouchParameterAvailable(r, key)) v[key] = 0;
  const width = target.width,
    height = target.height,
    mw = Math.min(width, 768),
    mh = Math.max(1, Math.round((height * mw) / width)),
    layer = retouchCanvas ?? canvas(width, height),
    mask = canvas(mw, mh),
    body = canvas(mw, mh),
    paint = canvas(mw, mh);
  retouchCanvas = layer;
  if (layer.width !== width) layer.width = width;
  if (layer.height !== height) layer.height = height;
  const gl = layer.getContext("webgl", {
    premultipliedAlpha: false,
    preserveDrawingBuffer: true,
    alpha: true,
    antialias: false,
  });
  if (!gl || !gl.getExtension("OES_texture_float")) {
    retouchCanvas = null;
    [layer, mask, body, paint].forEach((c) => {
      c.width = 0;
      c.height = 0;
    });
    throw new Error("retouch-webgl-unavailable");
  }
  const resources: WebGLTexture[] = [],
    shaders: WebGLShader[] = [],
    program = gl.createProgram()!,
    buffer = gl.createBuffer()!;
  try {
    const mc = mask.getContext("2d")!,
      bc = body.getContext("2d")!,
      pc = paint.getContext("2d")!;
    // 透明底色：alpha 专用于局部修复，因此其他通道使用 lighter 并在像素中独立编码。
    const faceSkin = canvas(mw, mh),
      eye = canvas(mw, mh),
      teeth = canvas(mw, mh),
      repair = canvas(mw, mh);
    const temp = [faceSkin, eye, teeth, repair];
    try {
      const sc = faceSkin.getContext("2d")!,
        ec = eye.getContext("2d")!,
        tc = teeth.getContext("2d")!,
        rc = repair.getContext("2d")!;
      for (const ctx of [sc, ec, tc, rc, bc, pc]) ctx.scale(mw, mh);
      for (const p of selectedRetouchFaces(r)) {
        const fw = Math.hypot(p[234].x - p[454].x, p[234].y - p[454].y),
          fh = Math.hypot(p[10].x - p[152].x, p[10].y - p[152].y);
        sc.fillStyle = "#fff";
        polygon(sc, p, FACE_OVAL);
        sc.fill();
        sc.globalCompositeOperation = "destination-out";
        for (const ids of [
          FACE_LEFT_EYE,
          FACE_RIGHT_EYE,
          FACE_LEFT_BROW,
          FACE_RIGHT_BROW,
          FACE_LIPS,
        ]) {
          polygon(sc, p, ids);
          sc.fill();
        }
        sc.globalCompositeOperation = "source-over";
        ec.fillStyle = "#fff";
        for (const ids of [FACE_LEFT_EYE, FACE_RIGHT_EYE]) {
          polygon(ec, p, ids);
          ec.fill();
        }
        tc.fillStyle = "#fff";
        polygon(tc, p, FACE_INNER_LIPS);
        tc.fill();
        rc.fillStyle = `rgba(255,255,255,${(v.darkCircles / 100) * 0.65})`;
        for (const i of [145, 374])
          ellipse(
            rc,
            { x: p[i].x, y: p[i].y + fh * 0.035 },
            fw * 0.14,
            fh * 0.045,
          );
        rc.fillStyle = `rgba(255,255,255,${(v.nasolabial / 100) * 0.65})`;
        for (const i of [206, 426]) ellipse(rc, p[i], fw * 0.07, fh * 0.14);
        const color = r.makeupColor;
        pc.fillStyle = color;
        pc.globalAlpha = (v.lipstick / 100) * 0.6;
        polygon(pc, p, FACE_LIPS);
        FACE_INNER_LIPS.forEach((i, j) =>
          j ? pc.lineTo(p[i].x, p[i].y) : pc.moveTo(p[i].x, p[i].y),
        );
        pc.closePath();
        pc.fill("evenodd");
        pc.globalAlpha = 1;
        pc.save();
        pc.filter = `blur(${Math.min(mw, mh) * 0.012}px)`;
        pc.fillStyle = color;
        pc.globalAlpha = (v.blush / 100) * 0.32;
        for (const i of [205, 425]) ellipse(pc, p[i], fw * 0.15, fh * 0.08);
        pc.fillStyle = "#603c30";
        pc.globalAlpha = (v.contour / 100) * 0.23;
        for (const i of [234, 454]) ellipse(pc, p[i], fw * 0.11, fh * 0.21);
        pc.fillStyle = "#fff0d1";
        pc.globalAlpha = (v.highlight / 100) * 0.35;
        for (const i of [6, 50, 280]) ellipse(pc, p[i], fw * 0.07, fh * 0.08);
        pc.fillStyle = "#684b6f";
        pc.globalAlpha = (v.eyeshadow / 100) * 0.35;
        for (const i of [159, 386])
          ellipse(
            pc,
            { x: p[i].x, y: p[i].y - fh * 0.025 },
            fw * 0.14,
            fh * 0.045,
          );
        pc.fillStyle = "#7a5343";
        pc.globalAlpha = (v.eyeBags / 100) * 0.3;
        for (const i of [145, 374])
          ellipse(
            pc,
            { x: p[i].x, y: p[i].y + fh * 0.025 },
            fw * 0.12,
            fh * 0.02,
          );
        pc.restore();
        pc.fillStyle = "#4b3027";
        pc.globalAlpha = (v.eyebrow / 100) * 0.6;
        for (const ids of [FACE_LEFT_BROW, FACE_RIGHT_BROW]) {
          polygon(pc, p, ids);
          pc.fill();
        }
        pc.globalAlpha = 1;
        for (const ids of [
          [33, 246, 161, 160, 159, 158, 157, 173, 133],
          [263, 466, 388, 387, 386, 385, 384, 398, 362],
        ]) {
          pc.strokeStyle = "#251a20";
          pc.lineJoin = "round";
          pc.lineWidth = fw * 0.008;
          pc.globalAlpha = (v.eyeliner / 100) * 0.85;
          pc.beginPath();
          ids.forEach((id, j) =>
            j ? pc.lineTo(p[id].x, p[id].y) : pc.moveTo(p[id].x, p[id].y),
          );
          pc.stroke();
          pc.globalAlpha = (v.eyelashes / 100) * 0.8;
          pc.lineWidth = fw * 0.004;
          for (const id of ids.slice(1, -1)) {
            pc.beginPath();
            pc.moveTo(p[id].x, p[id].y);
            pc.lineTo(
              p[id].x + (p[id].x - p[1].x) * 0.04,
              p[id].y - fh * 0.025,
            );
            pc.stroke();
          }
          pc.globalAlpha = 1;
        }
        pc.save();
        pc.beginPath();
        for (const ids of [FACE_LEFT_EYE, FACE_RIGHT_EYE]) {
          polygon(pc, p, ids, false);
        }
        pc.clip();
        pc.fillStyle = color;
        pc.globalAlpha = (v.contacts / 100) * 0.35;
        for (const i of [468, 473])
          ellipse(pc, p[i], fw * 0.035, (fw * 0.035 * width) / height);
        pc.restore();
        pc.fillStyle = "#784937";
        pc.globalAlpha = (v.freckles / 100) * 0.55;
        for (let j = 0; j < 42; j++) {
          const side = j % 2 ? 205 : 425,
            q = p[side],
            xx = Math.sin(j * 14.38) * 0.12,
            yy = Math.sin(j * 23.19) * 0.05;
          ellipse(
            pc,
            { x: q.x + xx * fw, y: q.y + yy * fh },
            fw * (0.0025 + (j % 3) * 0.0008),
            fh * 0.002,
          );
        }
        pc.globalAlpha = 1;
      }
      bc.fillStyle = "#fff";
      bc.strokeStyle = "#fff";
      for (const p of selectedRetouchBodies(r)) {
        if (retouchPointsVisible(p, [11, 12, 24, 23])) {
          polygon(bc, p, [11, 12, 24, 23]);
          bc.fill();
        }
        bc.lineCap = "round";
        bc.lineJoin = "round";
        const shoulder = Math.hypot(p[11].x - p[12].x, p[11].y - p[12].y);
        for (const [a, b] of [
          [11, 13],
          [13, 15],
          [12, 14],
          [14, 16],
          [23, 25],
          [25, 27],
          [24, 26],
          [26, 28],
        ])
          if (retouchPointsVisible(p, [a, b])) {
            bc.lineWidth = shoulder * (a < 20 ? 0.18 : 0.3);
            bc.beginPath();
            bc.moveTo(p[a].x, p[a].y);
            bc.lineTo(p[b].x, p[b].y);
            bc.stroke();
          }
      }
      const rgba = mc.createImageData(mw, mh),
        bodyPixels = bc.getImageData(0, 0, mw, mh),
        channels = temp.map(
          (c) => c.getContext("2d")!.getImageData(0, 0, mw, mh).data,
        );
      for (let i = 0; i < rgba.data.length; i += 4) {
        for (let ch = 0; ch < 3; ch++) rgba.data[i + ch] = channels[ch][i + 3];
        rgba.data[i + 3] = 255;
        bodyPixels.data[i] = bodyPixels.data[i + 3];
        bodyPixels.data[i + 1] = channels[3][i + 3];
        bodyPixels.data[i + 2] = 0;
        bodyPixels.data[i + 3] = 255;
      }
      mc.putImageData(rgba, 0, 0);
      bc.setTransform(1, 0, 0, 1, 0, 0);
      bc.putImageData(bodyPixels, 0, 0);
    } finally {
      temp.forEach((c) => {
        c.width = 0;
        c.height = 0;
      });
    }
    const shader = (type: number, source: string) => {
      const sh = gl.createShader(type)!;
      shaders.push(sh);
      gl.shaderSource(sh, source);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS))
        throw new Error(gl.getShaderInfoLog(sh) ?? "retouch-shader");
      return sh;
    };
    gl.attachShader(program, shader(gl.VERTEX_SHADER, vertex));
    gl.attachShader(program, shader(gl.FRAGMENT_SHADER, fragment));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS))
      throw new Error(gl.getProgramInfoLog(program) ?? "retouch-link");
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );
    const pos = gl.getAttribLocation(program, "position");
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);
    const texture = (name: string, unit: number, source: HTMLCanvasElement) => {
      const tex = gl.createTexture()!;
      resources.push(tex);
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.pixelStorei(
        gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL,
        name === "image" || name === "makeup",
      );
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        source,
      );
      gl.uniform1i(gl.getUniformLocation(program, name), unit);
    };
    texture("image", 0, target);
    texture("masks", 1, mask);
    texture("bodyMask", 2, body);
    texture("makeup", 3, paint);
    const warps = buildRetouchWarps(r, width / height),
      rows = Math.max(1, warps.length),
      floats = new Float32Array(3 * rows * 4);
    warps.forEach((w, i) =>
      floats.set(
        [
          w.x,
          w.y,
          w.rx,
          w.ry,
          w.sx,
          w.sy,
          w.dx,
          w.dy,
          w.angle,
          w.rotation,
          0,
          0,
        ],
        i * 12,
      ),
    );
    const tex = gl.createTexture()!;
    resources.push(tex);
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    // Float32Array 关键点纹理不能继承画布纹理的预乘 alpha 状态。
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      3,
      rows,
      0,
      gl.RGBA,
      gl.FLOAT,
      floats,
    );
    gl.uniform1i(gl.getUniformLocation(program, "warps"), 4);
    gl.uniform1i(gl.getUniformLocation(program, "count"), warps.length);
    gl.uniform1f(gl.getUniformLocation(program, "rows"), rows);
    gl.uniform1f(gl.getUniformLocation(program, "aspect"), width / height);
    gl.uniform2f(
      gl.getUniformLocation(program, "pixel"),
      1 / width,
      1 / height,
    );
    gl.uniform4f(
      gl.getUniformLocation(program, "skin"),
      v.smooth / 100,
      v.whiten / 100,
      v.even / 100,
      v.skinTone / 100,
    );
    gl.uniform4f(
      gl.getUniformLocation(program, "detail"),
      v.brightEyes / 100,
      v.whiteTeeth / 100,
      v.skinDetail / 100,
      v.bodySmooth / 100,
    );
    gl.uniform1f(
      gl.getUniformLocation(program, "bodyWhiten"),
      v.bodyWhiten / 100,
    );
    signal?.throwIfAborted();
    gl.viewport(0, 0, width, height);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    if (gl.getError() !== gl.NO_ERROR) throw new Error("retouch-render-failed");
    const ctx = target.getContext("2d")!;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = "copy";
    ctx.globalAlpha = 1;
    ctx.filter = "none";
    ctx.drawImage(layer, 0, 0);
    ctx.restore();
  } finally {
    resources.forEach((t) => gl.deleteTexture(t));
    shaders.forEach((s) => gl.deleteShader(s));
    gl.deleteProgram(program);
    gl.deleteBuffer(buffer);
    [mask, body, paint].forEach((c) => {
      c.width = 0;
      c.height = 0;
    });
  }
}
import { RETOUCH_GROUPS } from "./image-retouch";
const RETOUCH_GROUP_LOOKUP = Object.fromEntries(
  Object.entries(RETOUCH_GROUPS).flatMap(([group, keys]) =>
    keys.map((key) => [key, group]),
  ),
);
